import type React from 'react';
import { Text, View } from 'react-native';

import type { GlReport, GlReportAccount, GlReportLine } from '@/services/accounting-service';
import type { ReportDefinition } from '@/types/report-definition';
import { formatMoneyCents } from '@/utils/format-money';
import { groupByGlParentCode } from '@/utils/gl-hierarchy';

/**
 * The kind of a flattened GL row. The report groups postings under a per-GL
 * `header` and closes each group with a `total`; `opening` is the synthetic
 * "Beginning Balance" anchor, `line` is a real posting, `grand_total` is the
 * single QuickBooks-style "TOTAL" footer summing every account, and
 * `group_subtotal` rolls a dotted sub-account family (e.g. 1030 + 1030.05) into
 * a "Total <parent>" line beneath the indented children.
 */
export type GlRowKind = 'header' | 'opening' | 'line' | 'total' | 'grand_total' | 'group_subtotal';

/**
 * A flattened GL row — one ledger line, carrying its parent GL account's
 * code/name and the running balance through that line. `buildGlRows` also
 * emits synthetic `header`, `opening`, and `total` rows so the on-screen table
 * reads like a QuickBooks General Ledger (account header → beginning balance →
 * transactions → account total).
 */
export interface GlRow extends GlReportLine {
	id: string;
	kind: GlRowKind;
	glCodeId: string | null;
	glCode: string;
	glName: string;
	glAccountType: string;
	/** Running balance through this line within its GL account. */
	balanceCents: number;
	/**
	 * Sub-GL indent level. A parent account (or a flat/solo account) is depth 0;
	 * its dotted children (1030.05 under 1030) render at depth 1. Only the Name
	 * column's `cell` reads this — the export accessor stays unpadded.
	 */
	depth?: number;
}

const SOURCE_LABEL: Record<string, string> = {
	charge: 'Charge',
	payment: 'Payment',
	bill: 'Bill',
	bill_payment: 'Bill payment',
	credit: 'Credit',
	refund: 'Refund',
	bank_transfer: 'Transfer',
	import: 'Imported',
	manual: 'Manual',
	journal_entry: 'Journal',
};

const sourceLabel = (k: string): string => SOURCE_LABEL[k] ?? k;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The "Num" column should read as a plain transaction/check number. QB-import
// refs are stored as `<associationId>:<transNum>` (strip the prefix); native
// rows store their source domain-row UUID in `source_ref`, which is meaningless
// to a reader — blank those so Num only ever shows a real number.
export const glShortRef = (ref: string | null): string => {
	if (!ref) return '';
	const i = ref.lastIndexOf(':');
	const val = i >= 0 ? ref.slice(i + 1) : ref;
	return UUID_RE.test(val) ? '' : val;
};

/**
 * Display guard for the "#" column — the SOURCE DOCUMENT number (issue #486).
 *
 * Unlike `glShortRef` this must NOT split on ':'. Document numbers are
 * free-form user data (bill "07132026", check "1124", payment reference "ACH"
 * or "Zego: 4412"), so slicing at the last colon would silently truncate a
 * legitimate reference. Only the UUID blank carries over, as a backstop in case
 * a raw domain-row id ever reaches this column.
 */
export const glDocNumber = (ref: string | null | undefined): string =>
	(!ref || UUID_RE.test(ref) ? '' : ref);

/**
 * Flatten the GL Report's nested `accounts[].lines[]` into a single row list
 * shaped like a QuickBooks General Ledger: a per-account `header`, an optional
 * `opening` "Beginning Balance" anchor (only when the account carries an opening
 * balance — QB omits a $0 anchor), the postings with a running balance, a
 * per-account `total`, and one `grand_total` "TOTAL" footer across every account.
 *
 * Pure so it can be unit-tested without rendering the page. `selectedAccountId`
 * narrows to a single GL account (QB-style "General Ledger by Account");
 * `startMs` dates the synthetic rows.
 */
export function buildGlRows(
	report: GlReport | undefined,
	selectedAccountId: string | null,
	startMs: number,
): GlRow[] {
	if (!report) return [];
	const out: GlRow[] = [];
	let grandDebit = 0;
	let grandCredit = 0;
	let accountCount = 0;

	// Emit one account's QB-style block (header → optional opening → lines →
	// total) at the given sub-GL indent depth.
	const emitAccount = (acct: GlReportAccount, depth: number): void => {
		const key = acct.glCodeId ?? acct.code;
		const base = {
			glCodeId: acct.glCodeId,
			glCode: acct.code,
			glName: acct.name,
			glAccountType: acct.accountType,
			depth,
		};
		out.push({
			...base,
			id: `${key}:header`,
			kind: 'header',
			postedOn: startMs,
			debitCents: 0,
			creditCents: 0,
			sourceKind: 'header',
			sourceId: '',
			sourceRef: null,
			docNumber: null,
			counterparty: null,
			memo: null,
			balanceCents: acct.openingBalanceCents,
		});
		// QB shows a "Beginning Balance" line only when there IS one; a $0 anchor
		// is noise, so skip it and let the running balance start from the first line.
		if (acct.openingBalanceCents !== 0) {
			out.push({
				...base,
				id: `${key}:opening`,
				kind: 'opening',
				postedOn: startMs,
				debitCents: 0,
				creditCents: 0,
				sourceKind: 'opening',
				sourceId: '',
				sourceRef: null,
				docNumber: null,
				counterparty: null,
				memo: 'Beginning Balance',
				balanceCents: acct.openingBalanceCents,
			});
		}
		let running = acct.openingBalanceCents;
		for (let i = 0; i < acct.lines.length; i++) {
			const line = acct.lines[i];
			running += line.debitCents - line.creditCents;
			out.push({
				...line,
				...base,
				id: `${key}:${line.sourceKind}:${line.sourceId}:${i}`,
				kind: 'line',
				balanceCents: running,
			});
		}
		out.push({
			...base,
			id: `${key}:total`,
			kind: 'total',
			postedOn: startMs,
			debitCents: acct.periodDebitCents,
			creditCents: acct.periodCreditCents,
			sourceKind: 'total',
			sourceId: '',
			sourceRef: null,
			docNumber: null,
			counterparty: null,
			memo: null,
			balanceCents: acct.endingBalanceCents,
		});
	};

	// Group dotted sub-accounts (1030.05) under their pre-dot parent (1030) so
	// each family renders parent-first, children indented, then a roll-up.
	const scoped = report.accounts.filter((a) => !selectedAccountId || a.glCodeId === selectedAccountId);
	for (const { parentCode, parent, children } of groupByGlParentCode(scoped, (a) => a.code)) {
		const rendered = parent ? [parent, ...children] : children;
		for (const acct of rendered) {
			accountCount++;
			grandDebit += acct.periodDebitCents;
			grandCredit += acct.periodCreditCents;
			emitAccount(acct, acct === parent ? 0 : 1);
		}
		// Roll-up across the whole family — only when there's at least one dotted
		// child (a solo/flat account's own `total` already covers it). This is an
		// ADDITIONAL summary row, so it is NOT folded into the grand total.
		if (children.length > 0) {
			let subDebit = 0;
			let subCredit = 0;
			let subEnding = 0;
			for (const acct of rendered) {
				subDebit += acct.periodDebitCents;
				subCredit += acct.periodCreditCents;
				subEnding += acct.endingBalanceCents;
			}
			out.push({
				id: `${parentCode}:group_subtotal`,
				kind: 'group_subtotal',
				glCodeId: null,
				glCode: parentCode,
				glName: parent?.name ?? '',
				glAccountType: parent?.accountType ?? '',
				depth: 0,
				postedOn: startMs,
				debitCents: subDebit,
				creditCents: subCredit,
				sourceKind: 'group_subtotal',
				sourceId: '',
				sourceRef: null,
				docNumber: null,
				counterparty: null,
				memo: null,
				balanceCents: subEnding,
			});
		}
	}
	// Grand "TOTAL" footer across every account (QB shows it once at the bottom).
	// Skipped when scoped to a single account — that account's own total is it.
	if (accountCount > 0 && !selectedAccountId) {
		out.push({
			id: '__grand_total__',
			kind: 'grand_total',
			glCodeId: null,
			glCode: '',
			glName: '',
			glAccountType: '',
			postedOn: startMs,
			debitCents: grandDebit,
			creditCents: grandCredit,
			sourceKind: 'grand_total',
			sourceId: '',
			sourceRef: null,
			docNumber: null,
			counterparty: null,
			memo: null,
			balanceCents: grandDebit - grandCredit,
		});
	}
	return out;
}

/**
 * Row styling for the grouped view — passed to `ReportView.conditionalRowStyles`.
 * The `header` row reads as a section head (grey band + heavy top rule); the
 * `total` / `grand_total` rows are bold subtotals capped with a dark rule.
 * `fontWeight` cascades into the cells (RDT's cell style sets no weight of its
 * own), so the text goes bold without per-cell overrides.
 */
export const glRowConditionalStyles = [
	{
		when: (r: GlRow) => r.kind === 'header',
		style: {
			backgroundColor: '#eceff1',
			fontWeight: '700',
			borderTop: '2px solid #cfd8dc',
		},
	},
	{
		when: (r: GlRow) => r.kind === 'total',
		style: {
			backgroundColor: '#fafafa',
			fontWeight: '700',
			borderTop: '2px solid #000',
		},
	},
	{
		when: (r: GlRow) => r.kind === 'grand_total',
		style: {
			backgroundColor: '#eceff1',
			fontWeight: '700',
			borderTop: '3px double #000',
		},
	},
	{
		when: (r: GlRow) => r.kind === 'group_subtotal',
		style: {
			backgroundColor: '#f5f7fa',
			fontWeight: '600',
			borderTop: '1px solid #b0bec5',
		},
	},
];

// "Chrome" rows that carry a label + amounts but no per-line memo/type/date.
const isTotalRow = (k: GlRowKind): boolean => k === 'total' || k === 'grand_total' || k === 'group_subtotal';
const isSynthetic = (k: GlRowKind): boolean => k === 'header' || isTotalRow(k);

// Blank a debit/credit amount unless it's a positive real posting or a total
// row. Header/opening rows carry 0 and must render empty (not "$0.00").
const positiveMoney = (row: GlRow, cents: number): React.ReactNode => {
	if (isTotalRow(row.kind)) return formatMoneyCents(cents);
	if (row.kind === 'header' || row.kind === 'opening') return '';
	if (cents <= 0) return '';
	return formatMoneyCents(cents);
};

// The label a header / total / grand-total row shows in the Name column — the
// account isn't repeated on each line (QB style), so the group identity lives
// here instead. Exported so the print renderer shares the exact same labels.
export const glGroupLabel = (r: GlRow): string => {
	if (r.kind === 'header') return `${r.glCode} · ${r.glName}`;
	if (r.kind === 'total') return `Total ${r.glCode} · ${r.glName}`;
	if (r.kind === 'group_subtotal') return r.glName ? `Total ${r.glCode} · ${r.glName}` : `Total ${r.glCode}`;
	if (r.kind === 'grand_total') return 'TOTAL';
	return r.counterparty ?? '';
};

// On-screen Name cell: indent sub-accounts by their sub-GL depth. Only this
// display cell pads — the column `accessor` stays unpadded so exports and the
// print sheet get clean, flush labels.
const glNameCell = (r: GlRow): React.ReactNode => {
	const label = glGroupLabel(r);
	const depth = r.depth ?? 0;
	if (depth <= 0) return label;
	return <View style={{ paddingLeft: 16 * depth }}><Text>{label}</Text></View>;
};

export const glReport: ReportDefinition<GlRow> = {
	id: 'gl-report',
	title: 'General Ledger',
	subtitle: 'Every posting in the period, grouped by GL account with a running balance.',
	printUrl: (associationId, params) => {
		const sp = new URLSearchParams();
		if (params.startDate) sp.set('startDate', String(params.startDate));
		if (params.endDate) sp.set('endDate', String(params.endDate));
		if (params.basis) sp.set('basis', String(params.basis));
		const qs = sp.toString();
		return `/print/gl-report/${associationId}${qs ? `?${qs}` : ''}`;
	},
	// Layout matches QuickBooks' General Ledger: Date · # · Name · Memo · Debit ·
	// Credit · Balance. The GL account is a spanning header (in the Name column)
	// rather than repeated on each line, so GL Code / GL Name / Type default to
	// hidden and can be re-enabled via the column picker.
	//
	// v3 (issue #486): "#" — the source DOCUMENT number (bill #, check #, …) —
	// replaces "Num" (the entry_number sequence) as the default second column;
	// "Num" moves to the picker. The version MUST be bumped alongside a default
	// visibility change or every user who has ever opened the column picker keeps
	// their saved v2 set and never sees the new column.
	columnLayoutVersion: 'v3',
	columns: [
		{
			id: 'postedOn',
			header: 'Date',
			// Only real postings + the opening anchor carry a date; header/total
			// rows read as section chrome and stay blank.
			accessor: (r) => (r.kind === 'line' || r.kind === 'opening' ? r.postedOn : null),
			format: 'date',
			grow: 1,
			sortable: false,
		},
		{
			id: 'docNumber',
			header: '#',
			// The number written on the source document — the bill's number, the
			// check's number, the invoice's number, the owner's payment reference.
			// Blank on sources that have none (manual entries, refunds, openings).
			accessor: (r) => (r.kind === 'line' ? glDocNumber(r.docNumber) : ''),
			grow: 1,
			sortable: false,
		},
		{
			id: 'counterparty',
			header: 'Name',
			// Carries the account header / total labels — the group identity lives
			// here since the account isn't repeated on each line. The `cell` indents
			// dotted sub-accounts; `accessor` stays unpadded for clean exports.
			accessor: (r) => glGroupLabel(r),
			cell: (r) => glNameCell(r),
			grow: 3,
			wrap: true,
			sortable: false,
		},
		{
			id: 'memo',
			header: 'Memo',
			accessor: (r) => (r.kind === 'line' || r.kind === 'opening' ? r.memo ?? '' : ''),
			grow: 3,
			wrap: true,
			sortable: false,
		},
		{
			id: 'debit',
			header: 'Debit',
			accessor: (r) => (isSynthetic(r.kind) || r.debitCents > 0 ? r.debitCents : null),
			cell: (r) => positiveMoney(r, r.debitCents),
			format: 'money',
			grow: 1,
			sortable: false,
		},
		{
			id: 'credit',
			header: 'Credit',
			accessor: (r) => (isSynthetic(r.kind) || r.creditCents > 0 ? r.creditCents : null),
			cell: (r) => positiveMoney(r, r.creditCents),
			format: 'money',
			grow: 1,
			sortable: false,
		},
		{
			id: 'balance',
			header: 'Balance',
			// Header rows have no balance of their own; opening + lines + total carry
			// the running/ending figure, grand_total the net (debits − credits).
			accessor: (r) => (r.kind === 'header' ? null : r.balanceCents),
			format: 'money',
			grow: 1,
			sortable: false,
		},
		{
			id: 'sourceRef',
			header: 'Num',
			// The per-association journal_entries.entry_number sequence (QB's
			// Trans#). Superseded as the default column by "#" in v3 — it numbers
			// the ledger, not the paperwork — but kept in the picker for anyone
			// reconciling against a QB export. Empty on cash basis, which is a
			// projection with no journal entry behind it.
			accessor: (r) => (r.kind === 'line' ? glShortRef(r.sourceRef) : ''),
			grow: 1,
			defaultVisible: false,
			sortable: false,
		},
		{
			id: 'glCode',
			header: 'GL Code',
			accessor: (r) => (isTotalRow(r.kind) ? '' : r.glCode),
			grow: 1,
			defaultVisible: false,
			sortable: false,
		},
		{
			id: 'glName',
			header: 'GL Name',
			accessor: (r) => (isTotalRow(r.kind) ? '' : r.glName),
			grow: 2,
			defaultVisible: false,
			sortable: false,
		},
		{
			id: 'sourceKind',
			header: 'Type',
			accessor: (r) => (r.kind === 'line' ? sourceLabel(r.sourceKind) : ''),
			grow: 1,
			defaultVisible: false,
			sortable: false,
		},
		{
			id: 'glAccountType',
			header: 'Account Type',
			accessor: (r) => (isSynthetic(r.kind) ? '' : r.glAccountType),
			grow: 1,
			defaultVisible: false,
			sortable: false,
		},
	],
	// Excel / CSV: reuse the on-screen column accessors verbatim (they already
	// produce clean primitives — the `cell` JSX is display-only), dropping the
	// screen-only "Account Type" column and the visual header rows. The subtotal
	// + opening anchors stay so the export ties out.
	buildExport: (rows) => ({
		columns: glReport.columns
			.filter((c) => c.id !== 'glAccountType')
			.map((c) => ({ header: c.header, accessor: c.accessor, format: c.format })),
		rows: rows.filter((r) => r.kind !== 'header'),
	}),
};
